import {useEffect, useState} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getNodeByKey, $getSelection, $nodesOfType, BLUR_COMMAND, COMMAND_PRIORITY_LOW, FOCUS_COMMAND, HISTORY_MERGE_TAG, SELECTION_CHANGE_COMMAND} from 'lexical';
import {AssigneeSelectorMenu} from 'components';
import {$isMergeFieldNode} from 'components/lexical_editor/nodes/MergeFieldNode';
import {ImageNode} from '../nodes/ImageNode';
import { MergeFieldNode } from '../nodes/MergeFieldNode';
import {mergeRegister} from '@lexical/utils';
import _ from 'lodash';

export default function AssigneeSelectorMenuPlugin({companyDocument, employees}) {
  const [editor] = useLexicalComposerContext();
  const [selectedAssignableNode, setSelectedAssignableNode] = useState(null);

  const handleSelection = () => {
    const selection = $getSelection();

    if (!selection) return false;

    const nodes = selection.getNodes();
    if (nodes.length === 1 && nodes[0].assignable) {
      setSelectedAssignableNode(nodes[0]);
    } else {
      setSelectedAssignableNode(null);
    }

    return false;
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        handleSelection,
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        FOCUS_COMMAND,
        handleSelection,
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        BLUR_COMMAND,
        (e) => {          
          if (!e.relatedTarget?.closest('#editor-inner')) {
            setSelectedAssignableNode(null);
          }
          
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor]);

  useEffect(() => {
    function onMutation(mutations, {prevEditorState}) {
      for (const [nodeKey, mutation] of mutations) {
        const node = editor.getEditorState().read(() => $getNodeByKey(nodeKey));
        const assigneeId = node?.assigneeId;
        
        let prevAssigneeId;

        switch (mutation) {
          case 'created':
            if (!node.assignable) return;

            createRuleIfNecessary(assigneeId, nodeKey, node);
            updateNodeColor(assigneeId, node);
            break;
          case 'updated':
            prevAssigneeId = getPrevAssigneeId(prevEditorState, nodeKey);
            
            if (prevAssigneeId === assigneeId) continue;
            
            createRuleIfNecessary(assigneeId, nodeKey, node);
            updateNodeColor(assigneeId, node);
            removeRuleIfNecessary(prevAssigneeId);
            break;
          case 'destroyed':
            setSelectedAssignableNode(null);

            prevAssigneeId = getPrevAssigneeId(prevEditorState, nodeKey);
            removeRuleIfNecessary(prevAssigneeId);
            break;
        }

      }
    }

    return mergeRegister(
      editor.registerMutationListener(ImageNode, onMutation, {skipInitialization: true}),
      editor.registerMutationListener(MergeFieldNode, onMutation, {skipInitialization: true})
    );
  }, [editor]);

  const createRuleIfNecessary = (assigneeId, nodeKey, node) => {
    if (assigneeId && !hasField(assigneeId, nodeKey)) {
      const employee = _.find(employees, {id: assigneeId});
      companyDocument.addRule({...node.getAssignee(), employee});
    }
  }

  const removeRuleIfNecessary = (prevAssigneeId) => {
    if (prevAssigneeId && !hasField(prevAssigneeId)) {
      companyDocument.removeRule(prevAssigneeId);
    }
  }

  const updateNodeColor = (assigneeId, node) => {
    editor.update(() => {
      const assignmentRule = _.find(companyDocument.assignmentRules, {assignee: assigneeId});
      node.setColor(assigneeId ? assignmentRule.color : '#2EC0D5');
      setSelectedAssignableNode(node.getLatest());
    }, {tag: HISTORY_MERGE_TAG});
  }

  const getPrevAssigneeId = (prevState, nodeKey) => {
    return prevState.read(() => $getNodeByKey(nodeKey))?.assigneeId;
  };

  const onChange = (assignee) => {
    editor.update(() => selectedAssignableNode.setAssignee(assignee));
  };

  const hasField = (assigneeId, excludeNodeKey) => {
    let exists;

    editor.getEditorState().read(() => { 
      exists = $nodesOfType(MergeFieldNode).concat($nodesOfType(ImageNode)).some(
        node => node.getKey() !== excludeNodeKey && node.assigneeId === assigneeId
      );
    });

    return exists;
  };

  const position = () => {
    if (!selectedAssignableNode) return {};

    const el = editor.getElementByKey(selectedAssignableNode.getKey());
    const rect = el.getBoundingClientRect();
    const editorRect = document.getElementById('editor-inner').getBoundingClientRect();

    const position = {};
    position.top = rect.top - editorRect.top;
    const left = (rect.left - editorRect.left) + rect.width + 20;
    if (left <= editorRect.width - 260) {
      position.left = left;
    } else {
      position.right = rect.width + (editorRect.right - rect.right) + 20;
    }

    return position;
  };

  return <AssigneeSelectorMenu
    employees={employees}
    isOpen={!!selectedAssignableNode}
    assignee={selectedAssignableNode?.assigneeId}
    onChange={onChange}
    position={position()}
    selectedFieldType={$isMergeFieldNode(selectedAssignableNode) ? 'mergeField' : 'input'}
  />
}
